import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'
import {
  FaCalendarCheck,
  FaCheck,
  FaFistRaised,
  FaInstagram,
  FaPen,
  FaRegClock,
  FaShareAlt,
  FaTicketAlt,
  FaTrophy,
  FaUser,
} from 'react-icons/fa'
import { HiLocationMarker } from 'react-icons/hi'

import { BeltVisual } from '@goodfellas/belt-visual'
import { TrainingHeatmap } from '@goodfellas/training-heatmap-ui'
import type { TrainingHeatmapData } from '@goodfellas/training-history'

import type { FighterCard } from '@goodfellas/feedback-card-ui'

import Card from '../360-feedback/cards/Card'
import ShareCard from '../360-feedback/cards/ShareCard'
import {
  AddCompetition,
  CompetitionCard,
  CompetitionHistory,
  CompetitionStats,
} from '../360-feedback/competition'
import { CardExportProvider } from '../../contexts/CardExportContext'
import { goodfellasApi } from '../../apis'
import { GymResult, SearchResult, UserResult } from '../../constants/profiles'
import { fighterUrl, gymUrl } from '../../constants/shareLinks'
import {
  CompetitionMatch,
  calculateCompetitionStats,
} from '../../types/competition'

const TRAINING_MILESTONES = [1, 5, 10, 25, 50, 100, 150, 200, 250, 300]

interface VoucherItem {
  name: string
  description: string
  milestone: number
  redeemed: boolean
  createdAt: string
}

interface ProfileMini {
  uuid: string
  name: string
  image: string | null
  belt: { color: string; degree: number } | null
}

interface ChallengeItem {
  uuid: string
  status: string
  isChallenger: boolean
  opponent: ProfileMini | null
  created: string | null
  targetDate: string | null
  outcome?: 'won' | 'lost' | 'draw'
  disputed?: boolean
}

interface ChallengesData {
  pending: ChallengeItem[]
  completed: ChallengeItem[]
}

interface TrainingSessionItem {
  occurrenceId: string
  name: string | null
  location: string | null
  startsAt: string | null
  response: string | null
  checkedInAt?: string | null
}

interface TrainingStates {
  scheduled: TrainingSessionItem[]
  pending: TrainingSessionItem[]
  completed: TrainingSessionItem[]
}

interface Props {
  profile: SearchResult
  matches?: CompetitionMatch[]
  isOwner?: boolean
  onEditClick?: () => void
}

export default function PublicProfileView({
  profile,
  matches = [],
  isOwner,
  onEditClick,
}: Props) {
  const { t } = useTranslation('common')
  const isFighter = profile.type === 'FIGHTER'
  const stats = calculateCompetitionStats(matches)

  const [trainingData, setTrainingData] = useState<TrainingHeatmapData | null>(
    null,
  )
  const [trainingLoading, setTrainingLoading] = useState(false)
  const [trainingError, setTrainingError] = useState<string | null>(null)
  const [vouchers, setVouchers] = useState<VoucherItem[]>([])
  const [vouchersLoading, setVouchersLoading] = useState(false)
  const [vouchersError, setVouchersError] = useState<string | null>(null)
  const [fighterCard, setFighterCard] = useState<FighterCard | null>(null)
  const [fighterCardLoading, setFighterCardLoading] = useState(false)
  const [challenges, setChallenges] = useState<ChallengesData>({
    pending: [],
    completed: [],
  })
  const [challengesLoading, setChallengesLoading] = useState(false)
  const [trainingStates, setTrainingStates] = useState<TrainingStates>({
    scheduled: [],
    pending: [],
    completed: [],
  })
  const [activeTab, setActiveTab] = useState<string>('competition')
  const [showAddModal, setShowAddModal] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleShare = async () => {
    const id = String(profile.id)
    // O backend devolve `id = profileUserUuid || phoneNumber` (e ObjectId de
    // groupTable para academias legadas). Os paths canônicos /fighters|/gyms
    // são sequestrados pelo app (App/Universal Links), que só resolve uuids —
    // para ids não-uuid compartilha a URL da própria página, que continua
    // abrindo no site.
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    const url = !isUuid
      ? window.location.href
      : profile.type === 'GYM'
        ? gymUrl(id, 'web_profile')
        : fighterUrl(id, 'web_profile')
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({
          title: profile.name,
          text: String(t('search.share_profile_text', `Confira o perfil de ${profile.name} no Goodfellas`, { name: profile.name })),
          url,
        })
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch {
      // usuário cancelou o compartilhamento
    }
  }

  useEffect(() => {
    if (!isFighter || !profile.id) return
    const id = String(profile.id)
    const fetchTraining = async () => {
      setTrainingLoading(true)
      setTrainingError(null)
      try {
        const resp = await goodfellasApi.get(
          `/public/training/history/profile/${encodeURIComponent(id)}`,
          {
            validateStatus: () => true,
          },
        )
        if (resp.status >= 200 && resp.status < 300 && resp.data?.success) {
          setTrainingData(resp.data.data as TrainingHeatmapData)
        } else {
          setTrainingData(null)
        }
      } catch {
        setTrainingData(null)
        setTrainingError(
          t(
            'search.training_load_error',
            'Erro ao carregar frequência de treinos',
          ),
        )
      } finally {
        setTrainingLoading(false)
      }
    }
    const fetchVouchers = async () => {
      setVouchersLoading(true)
      setVouchersError(null)
      try {
        const resp = await goodfellasApi.get(
          `/public/vouchers/profile/${encodeURIComponent(id)}`,
          {
            validateStatus: () => true,
          },
        )
        if (
          resp.status >= 200 &&
          resp.status < 300 &&
          resp.data?.success &&
          Array.isArray(resp.data.data)
        ) {
          setVouchers(resp.data.data as VoucherItem[])
        } else {
          setVouchers([])
        }
      } catch {
        setVouchers([])
        setVouchersError(
          t('search.vouchers_load_error', 'Erro ao carregar vouchers'),
        )
      } finally {
        setVouchersLoading(false)
      }
    }
    const fetchFighterCard = async () => {
      setFighterCardLoading(true)
      try {
        const resp = await goodfellasApi.get(
          `/public/evaluation/cards/profile/${encodeURIComponent(id)}`,
          { validateStatus: () => true },
        )
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
          setFighterCard(resp.data as FighterCard)
        } else {
          setFighterCard(null)
        }
      } catch {
        setFighterCard(null)
      } finally {
        setFighterCardLoading(false)
      }
    }
    const fetchChallenges = async () => {
      setChallengesLoading(true)
      try {
        const resp = await goodfellasApi.get(
          `/public/profiles/${encodeURIComponent(id)}/challenges`,
          { validateStatus: () => true },
        )
        if (resp.status >= 200 && resp.status < 300 && resp.data?.success) {
          setChallenges(resp.data.data as ChallengesData)
        } else {
          setChallenges({ pending: [], completed: [] })
        }
      } catch {
        setChallenges({ pending: [], completed: [] })
      } finally {
        setChallengesLoading(false)
      }
    }
    const fetchTrainingStates = async () => {
      try {
        const resp = await goodfellasApi.get(
          `/public/profiles/${encodeURIComponent(id)}/trainings`,
          { validateStatus: () => true },
        )
        if (resp.status >= 200 && resp.status < 300 && resp.data?.success) {
          setTrainingStates(resp.data.data as TrainingStates)
        } else {
          setTrainingStates({ scheduled: [], pending: [], completed: [] })
        }
      } catch {
        setTrainingStates({ scheduled: [], pending: [], completed: [] })
      }
    }
    fetchTraining()
    fetchVouchers()
    fetchFighterCard()
    fetchChallenges()
    fetchTrainingStates()
  }, [isFighter, profile.id, t])

  const formatDate = (value: string | null | undefined): string | null => {
    if (!value) return null
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const outcomeLabel = (outcome?: 'won' | 'lost' | 'draw') => {
    if (outcome === 'won')
      return { text: t('search.result_won', 'Venceu'), cls: 'text-green-400' }
    if (outcome === 'lost')
      return { text: t('search.result_lost', 'Perdeu'), cls: 'text-red-400' }
    return { text: t('search.result_draw', 'Empate'), cls: 'text-white/60' }
  }

  const renderChallenge = (c: ChallengeItem) => {
    const opponentName =
      c.opponent?.name || t('search.opponent', 'Adversário')
    const when = formatDate(c.targetDate) || formatDate(c.created)
    const outcome = c.status === 'completed' ? outcomeLabel(c.outcome) : null
    return (
      <div
        key={c.uuid}
        className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3"
      >
        <div className="w-11 h-11 rounded-full overflow-hidden bg-gray-800 shrink-0 flex items-center justify-center">
          {c.opponent?.image ? (
            <img
              src={c.opponent.image}
              alt={opponentName}
              className="w-full h-full object-cover my-0"
            />
          ) : (
            <FaUser className="text-white/20" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">
              {c.isChallenger
                ? t('search.challenge_vs', 'vs {{name}}', {
                    name: opponentName,
                  })
                : t('search.challenge_from', 'de {{name}}', {
                    name: opponentName,
                  })}
            </span>
          </div>
          <div className="text-xs text-white/50 flex items-center gap-2">
            {when && <span>{when}</span>}
            {outcome && (
              <span className={`font-semibold ${outcome.cls}`}>
                {outcome.text}
              </span>
            )}
            {c.status === 'accepted' && (
              <span className="text-blue-400">
                {t('search.challenge_accepted', 'Aceito')}
              </span>
            )}
            {c.status === 'pending' && (
              <span className="text-yellow-500">
                {t('search.challenge_awaiting', 'Aguardando')}
              </span>
            )}
            {c.disputed && (
              <span className="text-orange-400">
                {t('search.challenge_disputed', 'Contestado')}
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderTraining = (
    s: TrainingSessionItem,
    state: 'scheduled' | 'pending',
  ) => {
    const when = formatDate(s.startsAt)
    return (
      <div
        key={s.occurrenceId}
        className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3"
      >
        <div
          className={`w-2 h-10 rounded-full shrink-0 ${
            state === 'scheduled' ? 'bg-blue-400' : 'bg-yellow-500'
          }`}
        />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">
            {s.name || t('search.training_one', 'Treino')}
          </div>
          <div className="text-xs text-white/50 flex items-center gap-2">
            {when && <span>{when}</span>}
            {s.location && <span className="truncate">· {s.location}</span>}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white pb-20">
      {/* Cover / Header */}
      <div className="relative h-64 md:h-80 bg-gradient-to-r from-red-900 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[url('/images/bg/gf-grad-red.jpg')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 -mt-32 relative z-10">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Sidebar / Profile Card */}
          <div className="w-full md:w-1/3 lg:w-1/4">
            <div className="bg-gray-900 border border-white/10 rounded-3xl p-6 shadow-2xl backdrop-blur-sm">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-6">
                  <div className="w-40 h-40 rounded-full border-4 border-red-600 p-1 bg-gray-900 shadow-xl overflow-hidden">
                    {profile.avatar ? (
                      <img
                        src={profile.avatar}
                        alt={profile.name}
                        className="w-full h-full rounded-full object-cover my-0"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center">
                        <FaUser className="text-5xl text-white/20" />
                      </div>
                    )}
                  </div>
                  {isFighter && (
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                      <BeltVisual
                        color={(profile as UserResult).belt}
                        size="sm"
                      />
                    </div>
                  )}
                </div>

                <h1 className="text-3xl font-serif font-bold mb-1">
                  {profile.name}
                </h1>
                <p className="text-red-500 font-sans font-semibold mb-4">
                  {isFighter ? (profile as UserResult).gym : t('search.gym')}
                </p>

                {profile.instagram && (
                  <a
                    href={`https://instagram.com/${profile.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-4 bg-white/5 px-4 py-2 rounded-xl"
                  >
                    <FaInstagram />
                    <span>
                      @
                      {profile.instagram.startsWith('@')
                        ? profile.instagram.slice(1)
                        : profile.instagram}
                    </span>
                  </a>
                )}

                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white transition-colors mb-4 px-6 py-2 rounded-xl border border-white/20"
                >
                  {copied ? (
                    <>
                      <FaCheck className="text-sm text-green-400" />
                      <span>{t('search.link_copied', 'Link copiado!')}</span>
                    </>
                  ) : (
                    <>
                      <FaShareAlt className="text-sm" />
                      <span>{t('search.share_profile', 'Compartilhar')}</span>
                    </>
                  )}
                </button>

                {isOwner && (
                  <button
                    onClick={onEditClick}
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white transition-colors mb-6 px-6 py-2 rounded-xl border border-white/20"
                  >
                    <FaPen className="text-sm" />
                    <span>Editar Perfil</span>
                  </button>
                )}

                {!isFighter && (profile as GymResult).location && (
                  <div className="flex items-center gap-2 text-white/60 mb-6">
                    <HiLocationMarker />
                    <span>{(profile as GymResult).location}</span>
                  </div>
                )}

                <div className="w-full pt-6 border-t border-white/5 space-y-4">
                  {isFighter ? (
                    <div className="flex justify-around">
                      <div className="text-center">
                        <div className="text-2xl font-bold">
                          {stats.totalMatches}
                        </div>
                        <div className="text-xs text-white/40 uppercase">
                          {t('search.matches')}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{stats.wins}</div>
                        <div className="text-xs text-white/40 uppercase">
                          {t('search.wins')}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{stats.losses}</div>
                        <div className="text-xs text-white/40 uppercase">
                          {t('search.losses') || 'DERROTAS'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        {(profile as GymResult).size}
                      </div>
                      <div className="text-xs text-white/40 uppercase">
                        {t('search.members')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-8 w-full min-w-0">
            {/* Sobre */}
            <div className="bg-gray-900/50 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
              <h2 className="text-2xl font-serif font-bold mb-4">
                {t('search.about')}
              </h2>
              <p className="text-white/70 leading-relaxed">
                {profile.bio || t('search.no_bio')}
              </p>
            </div>

            {/* Tabs Navigation */}
            {isFighter && (
              <div className="bg-gray-900/50 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm max-w-full">
                <div className="flex border-b border-white/10 overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('competition')}
                    className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                      activeTab === 'competition'
                        ? 'bg-red-600 text-white border-b-2 border-red-500'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <FaTrophy className="inline mr-2" />
                    {t('search.competition_history', 'Histórico de Competições')}
                  </button>
                  <button
                    onClick={() => setActiveTab('challenges')}
                    className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                      activeTab === 'challenges'
                        ? 'bg-red-600 text-white border-b-2 border-red-500'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <FaFistRaised className="inline mr-2" />
                    {t('search.challenges', 'Desafios')}
                  </button>
                  <button
                    onClick={() => setActiveTab('card')}
                    className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                      activeTab === 'card'
                        ? 'bg-red-600 text-white border-b-2 border-red-500'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {t('search.fighter_card', 'Card do Lutador')}
                  </button>
                  <button
                    onClick={() => setActiveTab('training')}
                    className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                      activeTab === 'training'
                        ? 'bg-red-600 text-white border-b-2 border-red-500'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {t('search.training_frequency', 'Frequência de Treinos')}
                  </button>
                  <button
                    onClick={() => setActiveTab('vouchers')}
                    className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                      activeTab === 'vouchers'
                        ? 'bg-red-600 text-white border-b-2 border-red-500'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <FaTicketAlt className="inline mr-2" />
                    {t('search.vouchers_and_achievements', 'Vouchers e Conquistas')}
                  </button>
                </div>

                <div className={`${activeTab === 'card' ? 'p-2' : 'p-8'} max-w-full overflow-hidden`}>
                  {/* Competition History Tab */}
                  {activeTab === 'competition' && (
                  <div className="space-y-6">
                    {isOwner ? (
                      // Own profile: full CRUD (add / edit / delete) on matches.
                      <CompetitionHistory
                        fighterUuid={String(profile.id)}
                        showActions
                        onAddClick={() => setShowAddModal(true)}
                      />
                    ) : matches.length > 0 ? (
                      <div className="space-y-8">
                        <CompetitionStats stats={stats} />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {matches.map((match) => (
                            <CompetitionCard
                              key={match.identificator.uuid}
                              match={match}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-900/30 border border-dashed border-white/10 rounded-3xl p-20 text-center">
                        <div className="w-20 h-20 mx-auto bg-gray-800 rounded-full flex items-center justify-center mb-4">
                          <FaTrophy className="text-white/10 text-3xl" />
                        </div>
                        <p className="text-white/40">{t('search.no_matches')}</p>
                      </div>
                    )}
                  </div>
            )}

            {/* Challenges Tab (desafios: pendentes e realizados) */}
            {activeTab === 'challenges' && (
              <div className="space-y-8">
                {challengesLoading &&
                !challenges.pending.length &&
                !challenges.completed.length ? (
                  <p className="text-white/50">
                    {t('search.loading', 'Carregando...')}
                  </p>
                ) : !challenges.pending.length &&
                  !challenges.completed.length ? (
                  <div className="bg-gray-900/30 border border-dashed border-white/10 rounded-3xl p-20 text-center">
                    <div className="w-20 h-20 mx-auto bg-gray-800 rounded-full flex items-center justify-center mb-4">
                      <FaFistRaised className="text-white/10 text-3xl" />
                    </div>
                    <p className="text-white/40">
                      {t('search.no_challenges', 'Nenhum desafio ainda.')}
                    </p>
                  </div>
                ) : (
                  <>
                    {challenges.pending.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-serif font-bold flex items-center gap-2">
                          <FaRegClock className="text-yellow-500" />
                          {t('search.challenges_pending', 'Pendentes')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {challenges.pending.map((c) => renderChallenge(c))}
                        </div>
                      </div>
                    )}
                    {challenges.completed.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-serif font-bold flex items-center gap-2">
                          <FaCheck className="text-green-500" />
                          {t('search.challenges_completed', 'Realizados')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {challenges.completed.map((c) => renderChallenge(c))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Training Frequency Tab */}
            {activeTab === 'training' && (
              <div className="space-y-4">
                {(trainingStates.scheduled.length > 0 ||
                  trainingStates.pending.length > 0) && (
                  <div className="space-y-6 mb-8">
                    {trainingStates.scheduled.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-serif font-bold flex items-center gap-2">
                          <FaCalendarCheck className="text-blue-400" />
                          {t('search.trainings_scheduled', 'Treinos marcados')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {trainingStates.scheduled.map((s) =>
                            renderTraining(s, 'scheduled'),
                          )}
                        </div>
                      </div>
                    )}
                    {trainingStates.pending.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-lg font-serif font-bold flex items-center gap-2">
                          <FaRegClock className="text-yellow-500" />
                          {t('search.trainings_pending', 'A confirmar')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {trainingStates.pending.map((s) =>
                            renderTraining(s, 'pending'),
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="text-sm text-white/50 mb-4">
                  {t('search.training_last_12_months', 'Últimos 12 meses')}
                </div>
                <div className="overflow-x-auto">
                  <TrainingHeatmap
                    data={trainingData}
                    loading={trainingLoading}
                    error={trainingError}
                    labels={{
                      total: t('search.training_total', 'Total') as string,
                      weeklyAvg: t(
                        'search.training_weekly_avg',
                        'Média sem.',
                      ) as string,
                      currentStreak: t(
                        'search.training_streak',
                        'Streak atual',
                      ) as string,
                      bestStreak: t(
                        'search.training_best_streak',
                        'Melhor streak',
                      ) as string,
                      less: t('search.less', 'Menos') as string,
                      more: t('search.more', 'Mais') as string,
                      training: t('search.training_one', 'treino') as string,
                      trainings: t('search.trainings', 'treinos') as string,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Vouchers Tab */}
            {activeTab === 'vouchers' && (
              <div className="space-y-4">
                {vouchersLoading && !vouchers.length ? (
                  <p className="text-white/50">
                    {t('search.loading', 'Carregando...')}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {trainingData?.stats && (
                      <div className="flex flex-wrap gap-2">
                        {TRAINING_MILESTONES.filter(
                          (m) => m <= trainingData.stats.total,
                        ).map((m) => (
                          <span
                            key={m}
                            className="bg-white/10 px-3 py-1.5 rounded-lg text-sm border border-white/10"
                          >
                            {m}{' '}
                            {m === 1
                              ? t('search.training_one', 'treino')
                              : t('search.trainings', 'treinos')}
                          </span>
                        ))}
                      </div>
                    )}
                    {vouchers.length > 0 && (
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {vouchers.map((v, i) => (
                          <li
                            key={i}
                            className="bg-white/5 px-4 py-3 rounded-xl text-sm border border-white/5 flex flex-col gap-1"
                          >
                            <span className="font-medium">{v.name}</span>
                            <span className="text-white/60">
                              {v.milestone} {t('search.trainings', 'treinos')}
                              {v.redeemed
                                ? ` · ${t('search.redeemed', 'Usado')}`
                                : ` · ${t('search.available', 'Disponível')}`}
                            </span>
                            {v.description && (
                              <span className="text-white/50 text-xs">
                                {v.description}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {profile.achievements &&
                      profile.achievements.length > 0 && (
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {profile.achievements.map((achievement, i) => (
                            <li
                              key={`ach-${i}`}
                              className="bg-white/5 px-4 py-3 rounded-xl text-sm border border-white/5 flex items-center gap-2"
                            >
                              <FaTrophy className="text-yellow-500 shrink-0" />
                              {achievement}
                            </li>
                          ))}
                        </ul>
                      )}
                    {!trainingData?.stats?.total &&
                      vouchers.length === 0 &&
                      (!profile.achievements ||
                        profile.achievements.length === 0) &&
                      !vouchersLoading && (
                        <p className="text-white/50">
                          {t(
                            'search.no_vouchers_achievements',
                            'Nenhum voucher ou conquista ainda.',
                          )}
                        </p>
                      )}
                  </div>
                )}
              </div>
            )}

            {/* Fighter Card Tab */}
            {activeTab === 'card' && (
              <CardExportProvider>
                <div className="space-y-4">
                  {fighterCardLoading ? (
                    <p className="text-white/50">
                      {t('search.loading', 'Carregando...')}
                    </p>
                  ) : fighterCard ? (
                    <>
                      <div className="flex justify-center">
                        <div id="cardContainer" className="w-full max-w-[320px] sm:max-w-[420px]">
                          <Card fighter={fighterCard} comparison={null} />
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row justify-center gap-4 mb-4">
                        <ShareCard.Button />
                        <ShareCard.ButtonHQ />
                      </div>
                      <ShareCard.Modal />
                    </>
                  ) : (
                    <p className="text-white/50">
                      {t(
                        'search.no_fighter_card',
                        'Card ainda não disponível.',
                      )}
                    </p>
                  )}
                </div>
              </CardExportProvider>
            )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isOwner && showAddModal && (
        <AddCompetition
          onClose={() => setShowAddModal(false)}
          onSuccess={() => setShowAddModal(false)}
        />
      )}
    </div>
  )
}
